import { cn } from "@/utils/cn";
import { IonIcon } from "@ionic/react";
import { cloudOfflineOutline, refreshOutline } from "ionicons/icons";
import React from "react";
import FilledButton from "../button/filled-button";
import { FullScreenBackgroundContainer } from "../container/full-screen-background-container";
import { PageTitleComponent } from "../page-title";

type OfflineScreenProps = {
  onRetry: () => void;
  remoteUrl?: string;
  isChecking?: boolean;
  errorMessage?: string;
};

export const OfflineScreen: React.FC<OfflineScreenProps> = ({
  onRetry,
  remoteUrl,
  isChecking = false,
  errorMessage,
}) => {
  return (
    <FullScreenBackgroundContainer
      imageClassName="bg-splash-screen-background"
      className="flex flex-col items-center justify-center px-4"
      backdrop={false}
    >
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md flex flex-col items-center gap-6">
        <div className="w-20 h-20 bg-danger-default rounded-full flex items-center justify-center animate-pulse">
          <IonIcon
            icon={cloudOfflineOutline}
            className="w-10 h-10 text-white"
          />
        </div>

        <PageTitleComponent
          title="You're offline"
          description="Please check your internet connection and try again."
          align="center"
          titleAlign="center"
          descriptionAlign="center"
          className="text-center"
        />

        <p className="text-medium-default text-center leading-relaxed">
          We couldn't reach the latest NutriCoach experience. Your local copy is
          still available, and we'll reconnect as soon as the network is back.
        </p>

        <div className="w-full flex flex-col gap-3">
          {remoteUrl && (
            <div className="rounded-xl border border-info-default/30 bg-info-default/10 p-4">
              <p className="text-sm text-dark-default">
                <span className="font-medium">Remote target:</span>{" "}
                <span className="break-words font-mono text-xs">
                  {remoteUrl}
                </span>
              </p>
            </div>
          )}

          {errorMessage && (
            <div className="rounded-xl border border-danger-default/30 bg-danger-default/10 p-4">
              <p className="text-sm text-dark-default">
                <span className="font-medium">Last attempt:</span>{" "}
                {errorMessage}
              </p>
            </div>
          )}
        </div>

        {/* Retry button with loading state */}
        <div className="w-full">
          <FilledButton
            type="button"
            color="dark"
            onClick={onRetry}
            disabled={isChecking}
            className={cn("w-full", {
              "opacity-70": isChecking,
            })}
          >
            <div className="flex items-center justify-center gap-2">
              {isChecking && (
                <IonIcon
                  icon={refreshOutline}
                  className="w-4 h-4 animate-spin"
                />
              )}
              {isChecking ? "Checking connection..." : "Try again"}
            </div>
          </FilledButton>
        </div>
      </div>
    </FullScreenBackgroundContainer>
  );
};
